import { useCallback } from 'react';

import shouldForceClickTrackInDev from '../../utils/shouldForceClickTrackInDev/shouldForceClickTrackInDev';
import useURLParam, { OverlayURLParam } from '../useURLParam/useURLParam';

export enum ClickTrackAction {
  AreaClicked = 'Area Clicked',
  Conversion = 'Conversion',
  HotspotClicked = 'Hotspot Clicked',
  ImageGallery = 'Image Gallery',
  MenuClicked = 'Menu Clicked',
  SceneViewed = 'Scene Viewed',
  TourStarted = 'Tour Started',
  VideoGallery = 'Video Gallery',
}

export const ILS_CATEGORY = 'LCP ILS';
export const PROPERTY_SITE_CATEGORY = 'LCP PropertySite';
export const MISSING_LABEL = 'no label';

interface ClickTrackEventDetails {
  actionId: string;
  label?: string;
}

/**
 * Sends events to RentCafe's ClickTrack tracker. RentCafe expects one call per
 * `ce_` key, all sharing the same category and action.
 */
const useClickTrackEvents = (analyticsEnabled: boolean) => {
  const propertyId = useURLParam(OverlayURLParam.PID);
  const isILS = useURLParam(OverlayURLParam.IS_ILS) === 'true';
  const debug = useURLParam(OverlayURLParam.CLICK_TRACK_DEBUG) === 'true';
  const enabled = analyticsEnabled || shouldForceClickTrackInDev(debug);

  return useCallback(
    (action: ClickTrackAction, details?: ClickTrackEventDetails) => {
      const tracker = window.ClickTrack;

      if (!enabled || !propertyId || !tracker) {
        if (debug) {
          console.warn(`[ClickTrack] skipped "${action}"`, {
            enabled,
            propertyId,
            trackerLoaded: Boolean(tracker),
          });
        }

        return;
      }

      const category = isILS ? ILS_CATEGORY : PROPERTY_SITE_CATEGORY;

      const trackEvent = (data: Record<string, string>) => {
        if (debug) {
          console.log('[ClickTrack]', category, action, data);
        }

        tracker._trackEvent(category, action, data);
      };

      trackEvent({ ce_propertyId: propertyId });

      if (!details) {
        return;
      }

      trackEvent({ ce_label: details.label || MISSING_LABEL });
      trackEvent({ ce_lcpactionid: details.actionId });
    },
    [debug, enabled, isILS, propertyId]
  );
};

export default useClickTrackEvents;
